import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openLink } from '@tma.js/sdk-react';
import { Crown, Layers, ListOrdered, Swords, Trophy } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import {
  useGetLinkQuery,
  useLazyStartLichessLinkQuery,
} from '@/services/qchessApi';
import { cn } from '@/lib/cn';

/**
 * QChess Hub section — rendered in the game-conditional area of the Hub when
 * Chess is the selected game (replaces the old standalone ChessHubTile pill).
 * Compact, matching the Clash Royale / Riot sections: a Lichess link-status
 * row + entry tiles to the chess sub-screens (tournaments / duels /
 * leaderboard). The full per-screen flows live behind these tiles.
 */
export function ChessHubSection() {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();

  const linkQuery = useGetLinkQuery(undefined, { refetchOnMountOrArgChange: true });
  const [startLink, { isFetching: starting }] = useLazyStartLichessLinkQuery();

  const linked = linkQuery.data?.linked ?? false;

  const onLink = async () => {
    haptics.impact('medium');
    try {
      const res = await startLink().unwrap();
      // Opens the Lichess authorize URL in the system browser (OAuth is blocked
      // inside the Telegram WebView). The callback returns to the mini app.
      openLink.ifAvailable(res.authorizeUrl);
    } catch {
      haptics.notification('error');
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Lichess link status now lives in the GameHeader (QLASH + Lichess ids).
          Here we only surface the connect action when the user isn't linked. */}
      {!linkQuery.isLoading && !linked && (
        <button
          type="button"
          onClick={() => void onLink()}
          disabled={starting}
          className="flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
        >
          <Crown aria-hidden className="h-4 w-4" />
          {starting ? t('home.linkOpening') : t('home.linkCta')}
        </button>
      )}

      <div className="grid grid-cols-2 gap-3">
        <ChessNav
          to="/chess/tournaments"
          icon={<Trophy aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          label={t('home.nav.tournaments')}
        />
        <ChessNav
          to="/chess/brackets"
          icon={<Layers aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          label={t('home.nav.brackets')}
        />
        <ChessNav
          to="/chess/challenges"
          icon={<Swords aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          label={t('home.nav.challenges')}
        />
        <ChessNav
          to="/chess/leaderboard"
          icon={<ListOrdered aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          label={t('home.nav.leaderboard')}
        />
      </div>
    </div>
  );
}

function ChessNav({ to, icon, label }: { to: string; icon: ReactNode; label: string }) {
  const haptics = useHaptics();
  return (
    <Link
      to={to}
      onClick={() => haptics.impact('light')}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-2xl border border-brand/40 bg-brand/5 px-2 py-3 text-center',
        'transition-opacity active:opacity-80',
      )}
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
        {icon}
      </span>
      <span className="text-[11px] font-bold text-fg">{label}</span>
    </Link>
  );
}
